import { c as _c } from "react/compiler-runtime";
import { Avatar, ChevronDown, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, LogOut, PlusCircleSolid, Settings, Settings3 } from '@/components';
import { CircularLoader } from '@/components/ui/CircularLoader';
import { LanguageFlagDropdown } from '@/components/ui/LanguageFlagDropdown';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { useI18n } from '@/context/I18nProvider';
import { useSubscription } from '@/context/SubscriptionProvider';
import { useCurrentUserRole } from '@/context/UserRoleProvider';
import { cn } from '@/lib/utils';
import { useClerk, useOrganization, useOrganizationList, useUser } from '@clerk/nextjs';
import { OrganizationMembershipResource } from '@clerk/types';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
const Header = () => {
  const $ = _c(82);
  const router = useRouter();
  const {
    user
  } = useUser();
  const clerk = useClerk();
  const [isFocused, setIsFocused] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const {
    t
  } = useI18n();
  const {
    isInternalUser,
    isLoading: isRoleLoading
  } = useCurrentUserRole();
  const {
    hasActiveSubscription
  } = useSubscription();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      userMemberships: {
        pageSize: 100
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    isLoaded,
    setActive,
    userMemberships
  } = useOrganizationList(t0);
  const {
    organization
  } = useOrganization();
  const scrollContainerRef = useRef(null);
  let t1;
  if ($[1] !== userMemberships) {
    t1 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20;
      if (isNearBottom && userMemberships.hasNextPage && !userMemberships.isFetching) {
        userMemberships.fetchNext();
      }
    };
    $[1] = userMemberships;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleScroll = t1;
  let t2;
  let t3;
  if ($[3] !== handleScroll) {
    t2 = () => {
      const container_0 = scrollContainerRef.current;
      if (container_0) {
        container_0.addEventListener("scroll", handleScroll);
        return () => container_0.removeEventListener("scroll", handleScroll);
      }
    };
    t3 = [handleScroll];
    $[3] = handleScroll;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!isLoaded) {
    return null;
  }
  let t4;
  if ($[6] !== setActive || $[7] !== t) {
    t4 = async selectedOrganization => {
      try {
        await setActive({
          organization: selectedOrganization.organization.id
        });
      } catch {
        toast.error(t("errors.switchOrganization"));
      }
    };
    $[6] = setActive;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleOrganizationClick = t4;
  let t5;
  if ($[9] !== clerk || $[10] !== t) {
    t5 = async () => {
      try {
        await clerk.signOut({
          redirectUrl: "/sign-in"
        });
      } catch {
        toast.error(t("errors.signOut"));
      }
    };
    $[9] = clerk;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSignOut = t5;
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsFocused(true);
    t7 = () => setIsFocused(false);
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  const t8 = `flex items-center gap-1.5 rounded-lg px-2 py-1.5 backdrop-blur hover:bg-accent ${isFocused ? "bg-white" : ""}`;
  let t9;
  if ($[14] !== t || $[15] !== user?.imageUrl) {
    t9 = user?.imageUrl && <Image src={user?.imageUrl} alt={t("ui.userAvatar")} width={24} height={24} />;
    $[14] = t;
    $[15] = user?.imageUrl;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <Avatar className="size-6">{t9}</Avatar>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = user?.fullName ?? user?.primaryEmailAddress?.emailAddress;
  let t12;
  if ($[19] !== t11) {
    t12 = <span className="text-sm font-semibold text-foreground hidden sm:inline">{t11}</span>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = profileOpen && "rotate-180";
  let t14;
  if ($[21] !== t13) {
    t14 = cn("size-4 text-foreground transition-transform duration-200", t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <ChevronDown className={t14} />;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t12 || $[27] !== t15 || $[28] !== t8) {
    t16 = <DropdownMenuTrigger asChild={true} onClick={t6} onBlur={t7}><button className={t8}>{t10}{t12}{t15}</button></DropdownMenuTrigger>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t15;
    $[28] = t8;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== organization || $[31] !== t) {
    t17 = organization && <><div className="flex flex-row items-center gap-2 justify-between min-h-9 h-fit"><div className="w-full flex items-center gap-2 px-3"><Avatar className="size-5">{organization?.imageUrl && <Image src={organization?.imageUrl} alt={t("ui.userAvatar")} width={24} height={24} />}</Avatar><span title={organization?.name} className="text-sm font-medium text-text-primary overflow-x-hidden text-ellipsis">{organization?.name}</span></div></div></>;
    $[30] = organization;
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== handleOrganizationClick || $[34] !== organization?.id || $[35] !== userMemberships.data || $[36] !== userMemberships.isFetching) {
    t18 = userMemberships.data.length > 1 && <><DropdownMenuSeparator /><div ref={scrollContainerRef} className={cn("flex flex-col overflow-y-auto", userMemberships.data.length > 6 && "h-[200px]")}>{userMemberships.data.filter(org => org.organization.id !== organization?.id).map(mem => <DropdownMenuItem key={mem.id} className="min-h-9 h-fit gap-1 cursor-pointer px-3" onClick={() => handleOrganizationClick(mem)}><div className="w-full flex items-center gap-2"><Avatar className="size-5"><Image src={mem.organization.imageUrl} alt={mem.organization.name} width={20} height={20} /></Avatar><span title={mem.organization.name} className="text-sm font-medium text-text-primary overflow-x-hidden text-ellipsis">{mem.organization.name}</span></div></DropdownMenuItem>)}{userMemberships.isFetching && <div className="flex justify-center py-2"><CircularLoader className="size-4" /></div>}</div></>;
    $[33] = handleOrganizationClick;
    $[34] = organization?.id;
    $[35] = userMemberships.data;
    $[36] = userMemberships.isFetching;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== organization) {
    t19 = organization ? <DropdownMenuSeparator /> : null;
    $[38] = organization;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== isInternalUser || $[41] !== isRoleLoading || $[42] !== router || $[43] !== t) {
    t20 = !isRoleLoading && isInternalUser && <DropdownMenuItem onClick={() => router.push("/admin")} className="cursor-pointer px-3"><Settings3 className="mr-2 size-4" />{t("navigation.admin")}</DropdownMenuItem>;
    $[40] = isInternalUser;
    $[41] = isRoleLoading;
    $[42] = router;
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== router) {
    t21 = () => router.push("/organizations/create");
    $[45] = router;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <PlusCircleSolid className="mr-2 size-4" />;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("header.addNewOrganization");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t23) {
    t24 = <DropdownMenuItem onClick={t21} className="cursor-pointer px-3">{t22}{t23}</DropdownMenuItem>;
    $[50] = t21;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== hasActiveSubscription || $[54] !== isInternalUser || $[55] !== router || $[56] !== t) {
    t25 = (hasActiveSubscription || isInternalUser) && <DropdownMenuItem onClick={() => router.push("/settings")} className="cursor-pointer px-3"><Settings className="mr-2 size-4" />{t("navigation.settings")}</DropdownMenuItem>;
    $[53] = hasActiveSubscription;
    $[54] = isInternalUser;
    $[55] = router;
    $[56] = t;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <LogOut className="mr-2 size-4" />;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t) {
    t27 = t("auth.signOut");
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== handleSignOut || $[62] !== t27) {
    t28 = <DropdownMenuItem className="text-destructive focus:text-destructive cursor-pointer px-3 focus:bg-destructive/10 rounded-none" onClick={handleSignOut}>{t26}{t27}</DropdownMenuItem>;
    $[61] = handleSignOut;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t20 || $[65] !== t24 || $[66] !== t25 || $[67] !== t28) {
    t29 = <div className="bg-muted -m-1">{t20}{t24}{t25}{t28}</div>;
    $[64] = t20;
    $[65] = t24;
    $[66] = t25;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t17 || $[70] !== t18 || $[71] !== t19 || $[72] !== t29) {
    t30 = <DropdownMenuContent align="start" className="w-[260px] rounded-2xl shadow-md">{t17}{t18}{t19}{t29}</DropdownMenuContent>;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== profileOpen || $[75] !== t16 || $[76] !== t30) {
    t31 = <DropdownMenu open={profileOpen} onOpenChange={setProfileOpen}>{t16}{t30}</DropdownMenu>;
    $[74] = profileOpen;
    $[75] = t16;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  let t33;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <ThemeToggle className="mr-1" />;
    t33 = <LanguageFlagDropdown chevronPosition="left" dropdownAlign="end" buttonClassName="mr-2 md:mr-4" />;
    $[78] = t32;
    $[79] = t33;
  } else {
    t32 = $[78];
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t31) {
    t34 = <header className="flex w-full items-center justify-between pl-3 md:pl-5"><div className="flex w-full items-center justify-between h-[36px]">{t31}{t32}{t33}</div></header>;
    $[80] = t31;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  return t34;
};
export { Header };